import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  ChevronUp,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import type { QuizQuestion } from "@/lib/quiz-generator";

type PolicyQuiz = Database["public"]["Tables"]["policy_quizzes"]["Row"];
type PolicyRow = Database["public"]["Tables"]["policies"]["Row"];

function generateQuestionId() {
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export default function QuizEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile, hasAnyRole } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleDelete = async () => {
    if (!quiz || !canEdit) return;
    setDeleting(true);
    try {
      const { error: deleteErr } = await supabase
        .from("policy_quizzes")
        .delete()
        .eq("id", quiz.id);

      if (deleteErr) throw deleteErr;

      toast({
        title: "Quiz deleted",
        description: "The quiz has been removed.",
      });
      navigate("/quizzes");
    } catch (err: any) {
      console.error("Error deleting quiz:", err);
      toast({
        title: "Delete failed",
        description: err?.message ?? "Could not delete quiz.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };
  const [quiz, setQuiz] = useState<PolicyQuiz | null>(null);
  const [policy, setPolicy] = useState<PolicyRow | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (profile?.company_id && id) {
      void loadQuiz();
    }
  }, [profile?.company_id, id]);

  const loadQuiz = async () => {
    if (!profile?.company_id || !id) return;
    setLoading(true);
    setError(null);
    try {
      const { data: quizData, error: quizErr } = await supabase
        .from("policy_quizzes")
        .select("*")
        .eq("company_id", profile.company_id)
        .eq("id", id)
        .maybeSingle();

      if (quizErr) throw quizErr;
      if (!quizData) {
        setError("Quiz not found or no longer available.");
        setQuiz(null);
        setPolicy(null);
        return;
      }

      const q = quizData as PolicyQuiz;
      const { data: policyData, error: policyErr } = await supabase
        .from("policies")
        .select("*")
        .eq("id", q.policy_id)
        .maybeSingle();

      if (policyErr) throw policyErr;

      const p = policyData as PolicyRow | null;
      const isCreator = p?.created_by === profile.id;
      const isReviewer = p?.assigned_reviewers?.includes(profile.id) ?? false;
      const isApprover = p?.assigned_approvers?.includes(profile.id) ?? false;
      const isAdmin = hasAnyRole(["admin"]);
      const allowed = isCreator || isReviewer || isApprover || isAdmin;

      if (!allowed) {
        setError("You don't have permission to edit this quiz.");
        setCanEdit(false);
      } else {
        setCanEdit(true);
      }

      const parsedQuestions: QuizQuestion[] = Array.isArray((q as any).questions)
        ? (q as any).questions
        : [];

      setQuiz(q);
      setPolicy(p ?? null);
      setTitle(q.title);
      setDescription(q.description ?? "");
      setQuestions(
        parsedQuestions.length > 0
          ? parsedQuestions
          : [
              {
                id: generateQuestionId(),
                question: "",
                options: ["", "", "", ""],
                correctIndex: 0,
                explanation: "",
                points: 10,
              },
            ]
      );
    } catch (err: any) {
      console.error("Error loading quiz:", err);
      setError(err?.message ?? "Failed to load quiz.");
      setQuiz(null);
      setPolicy(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!quiz || !profile?.company_id || !canEdit) return;

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast({
        title: "Validation error",
        description: "Quiz title is required.",
        variant: "destructive",
      });
      return;
    }

    const validQuestions = questions.filter(
      (q) =>
        q.question.trim().length > 0 &&
        q.options.every((o) => o.trim().length > 0) &&
        q.options[q.correctIndex]?.trim().length > 0
    );

    if (validQuestions.length === 0) {
      toast({
        title: "Validation error",
        description: "Add at least one valid question with all options filled.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const { error: updateErr } = await supabase
        .from("policy_quizzes")
        .update({
          title: trimmedTitle,
          description: description.trim() || null,
          questions: validQuestions,
        })
        .eq("id", quiz.id);

      if (updateErr) throw updateErr;

      toast({
        title: "Quiz saved",
        description: "Your changes have been saved.",
      });
      setQuiz({ ...quiz, title: trimmedTitle, description: description.trim() || null });
      setQuestions(validQuestions);
    } catch (err: any) {
      console.error("Error saving quiz:", err);
      toast({
        title: "Save failed",
        description: err?.message ?? "Could not save quiz.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (index: number, updates: Partial<QuizQuestion>) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, ...updates } : q))
    );
  };

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        id: generateQuestionId(),
        question: "",
        options: ["", "", "", ""],
        correctIndex: 0,
        explanation: "",
        points: 10,
      },
    ]);
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const moveQuestion = (from: number, to: number) => {
    if (to < 0 || to >= questions.length) return;
    setQuestions((prev) => {
      const next = [...prev];
      const [removed] = next.splice(from, 1);
      next.splice(to, 0, removed);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/quizzes">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to quizzes
          </Link>
        </Button>
        <Alert variant="destructive">
          <AlertDescription>{error ?? "Quiz not found."}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/quizzes">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to quizzes
          </Link>
        </Button>
        <Alert variant="destructive">
          <AlertDescription>
            You don't have permission to edit this quiz. Only the policy creator,
            reviewers, and approvers can edit.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/quizzes">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to quizzes
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete quiz
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete quiz?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the quiz and all attempt records. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void handleDelete();
                  }}
                  disabled={deleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quiz details</CardTitle>
          {policy && (
            <p className="text-sm text-muted-foreground">
              From policy: {policy.title}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="quiz-title">Title</Label>
            <Input
              id="quiz-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Quiz title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="quiz-desc">Description</Label>
            <Textarea
              id="quiz-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the quiz"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Questions</CardTitle>
          <Button variant="outline" size="sm" onClick={addQuestion}>
            <Plus className="w-4 h-4 mr-2" />
            Add question
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {questions.map((q, index) => (
            <Card key={q.id} className="border-dashed">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">
                    Question {index + 1}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveQuestion(index, index - 1)}
                      disabled={index === 0}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveQuestion(index, index + 1)}
                      disabled={index === questions.length - 1}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => removeQuestion(index)}
                      disabled={questions.length <= 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Question text</Label>
                  <Input
                    value={q.question}
                    onChange={(e) =>
                      updateQuestion(index, { question: e.target.value })
                    }
                    placeholder="Enter the question"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Options (select the correct one below)</Label>
                  <div className="grid gap-2">
                    {[0, 1, 2, 3].map((optIdx) => (
                      <Input
                        key={optIdx}
                        value={q.options[optIdx] ?? ""}
                        onChange={(e) => {
                          const next = [...q.options];
                          next[optIdx] = e.target.value;
                          updateQuestion(index, { options: next });
                        }}
                        placeholder={`Option ${optIdx + 1}`}
                        className={
                          q.correctIndex === optIdx
                            ? "border-primary bg-primary/5"
                            : ""
                        }
                      />
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Correct answer</Label>
                  <Select
                    value={String(q.correctIndex)}
                    onValueChange={(v) =>
                      updateQuestion(index, { correctIndex: parseInt(v, 10) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {q.options.map((opt, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {opt?.trim() || `Option ${i + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Explanation (shown after answering)</Label>
                  <Textarea
                    value={q.explanation}
                    onChange={(e) =>
                      updateQuestion(index, { explanation: e.target.value })
                    }
                    placeholder="Explain why this is the correct answer"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Points</Label>
                  <Input
                    type="number"
                    min={1}
                    value={q.points}
                    onChange={(e) =>
                      updateQuestion(index, {
                        points: Math.max(1, parseInt(e.target.value, 10) || 10),
                      })
                    }
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save changes
        </Button>
      </div>
    </div>
  );
}
